import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Bell, BellRing, Phone, Check, Clock, X, Loader2, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const POLL_MS = 30000;

function fmtRel(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMin = Math.round((d - new Date()) / 60000);
    if (diffMin <= 0) {
      const past = -diffMin;
      if (past < 60) return `${past} min fa`;
      const h = Math.floor(past / 60);
      if (h < 24) return `${h}h fa`;
      return `${Math.floor(h / 24)}g fa`;
    }
    if (diffMin < 60) return `tra ${diffMin} min`;
    return `tra ${Math.floor(diffMin / 60)}h ${diffMin % 60}m`;
  } catch { return iso; }
}

const SNOOZE_PRESETS = [
  { label: "1 ora", minutes: 60 },
  { label: "4 ore", minutes: 240 },
  { label: "Domani 9:00", minutes: null, tomorrowMorning: true },
];

function nextMorningMinutes() {
  const now = new Date();
  const target = new Date(now);
  target.setDate(now.getDate() + 1);
  target.setHours(9, 0, 0, 0);
  return Math.max(1, Math.round((target - now) / 60000));
}

export default function FollowupBell() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "operatore";
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState({ pending: 0, due: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(null); // id while updating
  const [snoozeMenuFor, setSnoozeMenuFor] = useState(null);
  const wrapperRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/followups/count");
      setCount(data);
    } catch { /* silent */ }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/followups", { params: { status: "pending", limit: 25 } });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial + polling counter
  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, POLL_MS);
    return () => clearInterval(t);
  }, [loadCount]);

  // Load items when dropdown opens
  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSnoozeMenuFor(null);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleDone = async (id) => {
    setWorking(id);
    try {
      await api.post(`/followups/${id}/done`, { note: "" });
      toast.success("Promemoria chiuso");
      setItems((arr) => arr.filter((it) => it.id !== id));
      loadCount();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(null);
    }
  };

  const handleSnooze = async (id, minutes) => {
    setWorking(id);
    try {
      await api.post(`/followups/${id}/snooze`, { minutes });
      toast.success("Promemoria posticipato");
      setSnoozeMenuFor(null);
      loadItems();
      loadCount();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(null);
    }
  };

  const handleDismiss = async (id) => {
    setWorking(id);
    try {
      await api.delete(`/followups/${id}`);
      setItems((arr) => arr.filter((it) => it.id !== id));
      loadCount();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(null);
    }
  };

  const dueCount = count.due || 0;
  const totalPending = count.pending || 0;
  const Icon = dueCount > 0 ? BellRing : Bell;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-black/5 transition"
        data-testid="followup-bell"
        aria-label="Promemoria chiamate"
        title={dueCount > 0 ? `${dueCount} promemoria da gestire` : "Nessun promemoria attivo"}
      >
        <Icon className={`w-5 h-5 ${dueCount > 0 ? "text-orange-600 animate-pulse" : ""}`} />
        {totalPending > 0 && (
          <span
            data-testid="followup-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white"
            style={{ background: dueCount > 0 ? "#DC2626" : "#6B7280", padding: "0 5px" }}
          >
            {totalPending}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[400px] max-h-[520px] bg-white rounded-xl shadow-2xl border z-50 flex flex-col"
          style={{ borderColor: "var(--border)" }}
          data-testid="followup-dropdown"
        >
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <div>
              <div className="font-bold text-sm" style={{ fontFamily: "Manrope" }}>Promemoria chiamate</div>
              <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                {dueCount} da chiamare ora · {totalPending} totali
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-black/5" data-testid="followup-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Nessun promemoria attivo.<br />
                Vengono creati automaticamente all'invio di un messaggio WhatsApp.
              </div>
            )}
            {items.map((it) => {
              const isDue = it.is_due;
              return (
                <div
                  key={it.id}
                  className="px-4 py-3 border-b last:border-b-0"
                  style={{ borderColor: "var(--border)", background: isDue ? "rgba(254,242,242,0.5)" : "white" }}
                  data-testid={`followup-item-${it.id}`}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <div className="p-1.5 rounded-md flex-shrink-0" style={{ background: isDue ? "#FEE2E2" : "#F3F4F6" }}>
                      <Phone className="w-3.5 h-3.5" style={{ color: isDue ? "#991B1B" : "#374151" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/clienti/${it.cliente_id}`}
                        className="font-semibold text-sm hover:underline block truncate"
                        onClick={() => setOpen(false)}
                        data-testid={`followup-link-${it.id}`}
                      >
                        {it.cliente_nome || "Cliente"}
                      </Link>
                      <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>
                        {it.cliente_telefono || "—"}
                      </div>
                    </div>
                    <span
                      className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                      style={{ background: isDue ? "#DC2626" : "#E5E7EB", color: isDue ? "white" : "#374151" }}
                    >
                      {isDue ? "Da chiamare" : "In attesa"}
                    </span>
                  </div>

                  {it.message_preview && (
                    <div className="text-[11px] mb-2 p-2 rounded" style={{ background: "#F3F4F6", color: "#374151" }}>
                      <MessageCircle className="w-3 h-3 inline mr-1" />
                      {it.message_preview}
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {fmtRel(it.snoozed_until || it.due_at)}
                    </span>
                    {canManage && (
                      <div className="flex items-center gap-1 relative">
                        <button
                          onClick={() => handleDone(it.id)}
                          disabled={working === it.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold text-white hover:brightness-110 disabled:opacity-50"
                          style={{ background: "#065F46" }}
                          data-testid={`followup-done-${it.id}`}
                        >
                          {working === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Chiamato
                        </button>
                        <button
                          onClick={() => setSnoozeMenuFor(snoozeMenuFor === it.id ? null : it.id)}
                          disabled={working === it.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] font-semibold hover:bg-black/5 disabled:opacity-50"
                          style={{ borderColor: "var(--border)" }}
                          data-testid={`followup-snooze-${it.id}`}
                        >
                          <Clock className="w-3 h-3" /> Posticipa
                        </button>
                        <button
                          onClick={() => handleDismiss(it.id)}
                          disabled={working === it.id}
                          className="p-1 rounded hover:bg-black/5 disabled:opacity-50"
                          title="Elimina promemoria"
                          data-testid={`followup-dismiss-${it.id}`}
                        >
                          <X className="w-3.5 h-3.5" style={{ color: "#991B1B" }} />
                        </button>

                        {snoozeMenuFor === it.id && (
                          <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg p-1 z-10" style={{ borderColor: "var(--border)" }}>
                            {SNOOZE_PRESETS.map((p) => (
                              <button
                                key={p.label}
                                onClick={() => handleSnooze(it.id, p.tomorrowMorning ? nextMorningMinutes() : p.minutes)}
                                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 rounded whitespace-nowrap"
                                data-testid={`followup-snooze-preset-${it.id}-${p.minutes || "tomorrow"}`}
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
